/**
 * PipelinesSettingsPage — manage fias_leads_pipelines + stages.
 * Create / rename pipelines, set default, add / edit / delete / reorder
 * stages.
 *
 * Name fields (pipeline + stage) are *controlled* inputs backed by local
 * state seeded from the server row. They persist on blur / Enter and only
 * fire a mutation when the value actually changed. The previous version
 * used uncontrolled `defaultValue` inputs, which silently dropped edits
 * when the list re-rendered after a sibling mutation — that was the
 * "renames don't save" bug.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsPipelines,
  useCreateFiasLeadsPipeline,
  useUpdateFiasLeadsPipeline,
  useDeleteFiasLeadsPipeline,
  useAddFiasLeadsStage,
  useUpdateFiasLeadsStage,
  useDeleteFiasLeadsStage,
  useReorderFiasLeadsStages,
} from '../hooks/useFiasLeads';
import type {
  FiasLeadsPipelineDto,
  FiasLeadsPipelineStageDto,
  FiasLeadsPipelineStageType,
} from '@/lib/api';
import { SkeletonBlock } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const STAGE_TYPE_COLORS: Record<FiasLeadsPipelineStageType, string> = {
  open: 'bg-blue-100 text-blue-700',
  won: 'bg-emerald-100 text-emerald-700',
  lost: 'bg-rose-100 text-rose-700',
};

export function PipelinesSettingsPage({ holderId }: Props) {
  const pipelinesQuery = useFiasLeadsPipelines(holderId);
  const createPipeline = useCreateFiasLeadsPipeline(holderId);

  const [newPipelineName, setNewPipelineName] = useState('');

  const handleCreatePipeline = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPipelineName.trim()) return;
    await createPipeline.mutateAsync({ name: newPipelineName.trim() });
    setNewPipelineName('');
  };

  if (pipelinesQuery.isLoading) return <SkeletonBlock lines={6} />;

  const pipelines = pipelinesQuery.data ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto flex flex-col gap-6">
      <Link
        to={`${ARCHE_PATH}/settings`}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline w-fit"
      >
        <ArrowLeft className="h-4 w-4" />
        Settings
      </Link>

      <header>
        <h1 className="text-2xl font-semibold">Pipelines</h1>
        <p className="text-sm text-muted-foreground">
          Sales pipelines and their stages. Holder-scoped.
        </p>
      </header>

      <form
        onSubmit={handleCreatePipeline}
        className="flex gap-2 rounded border border-border bg-card p-4"
      >
        <input
          type="text"
          placeholder="New pipeline name"
          value={newPipelineName}
          onChange={(e) => setNewPipelineName(e.target.value)}
          className="flex-1 rounded border border-border bg-background px-3 py-2 text-sm"
          required
        />
        <button
          type="submit"
          disabled={!newPipelineName.trim() || createPipeline.isPending}
          className="rounded bg-primary px-4 py-2 text-primary-foreground text-sm"
        >
          {createPipeline.isPending ? 'Creating…' : 'Create pipeline'}
        </button>
      </form>

      {pipelines.length === 0 && <p className="text-sm text-muted-foreground">No pipelines yet.</p>}

      {pipelines.map((p) => (
        <PipelineCard key={p.id} holderId={holderId} pipeline={p} />
      ))}
    </div>
  );
}

function PipelineCard({
  holderId,
  pipeline,
}: {
  holderId: string;
  pipeline: FiasLeadsPipelineDto;
}) {
  const updatePipeline = useUpdateFiasLeadsPipeline(holderId);
  const deletePipeline = useDeleteFiasLeadsPipeline(holderId);
  const addStage = useAddFiasLeadsStage(holderId);
  const updateStage = useUpdateFiasLeadsStage(holderId);
  const deleteStage = useDeleteFiasLeadsStage(holderId);
  const reorderStages = useReorderFiasLeadsStages(holderId);

  // Stages arrive pre-sorted by display_order from the API.
  const stages = pipeline.stages;

  const moveStage = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= stages.length) return;
    const reordered = [...stages];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(target, 0, moved);
    try {
      await reorderStages.mutateAsync({
        pipelineId: pipeline.id,
        stages: reordered.map((s, i) => ({ stageId: s.id, displayOrder: i })),
      });
    } catch (err) {
      alert(`Could not reorder stages: ${(err as Error).message}`);
    }
  };

  return (
    <div className="rounded border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-1 items-center gap-2">
          <PipelineNameEditor
            pipeline={pipeline}
            onRename={(name) =>
              updatePipeline.mutateAsync({ pipelineId: pipeline.id, input: { name } })
            }
          />
          {pipeline.is_default && (
            <span className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary">default</span>
          )}
        </div>
        <div className="flex gap-2">
          {!pipeline.is_default && (
            <button
              onClick={() =>
                updatePipeline.mutateAsync({
                  pipelineId: pipeline.id,
                  input: { isDefault: true },
                })
              }
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Make default
            </button>
          )}
          <button
            onClick={() => {
              if (
                confirm(
                  `Delete pipeline "${pipeline.name}"? Deals on this pipeline will be cascade-deleted.`,
                )
              ) {
                deletePipeline.mutateAsync(pipeline.id);
              }
            }}
            className="text-xs text-muted-foreground hover:text-destructive"
          >
            Delete
          </button>
        </div>
      </div>

      <StaleAlertSetting
        pipeline={pipeline}
        onChange={(staleAfterDays) =>
          updatePipeline.mutateAsync({ pipelineId: pipeline.id, input: { staleAfterDays } })
        }
      />

      <ul className="flex flex-col gap-1">
        {stages.map((s, index) => (
          <StageRow
            key={s.id}
            stage={s}
            isFirst={index === 0}
            isLast={index === stages.length - 1}
            reordering={reorderStages.isPending}
            onMoveUp={() => moveStage(index, -1)}
            onMoveDown={() => moveStage(index, 1)}
            onRename={(name) =>
              updateStage.mutateAsync({ pipelineId: pipeline.id, stageId: s.id, input: { name } })
            }
            onChangeType={(stageType) =>
              updateStage.mutateAsync({
                pipelineId: pipeline.id,
                stageId: s.id,
                input: { stageType },
              })
            }
            onDelete={() => {
              if (confirm(`Delete stage "${s.name}"?`)) {
                deleteStage.mutateAsync({ pipelineId: pipeline.id, stageId: s.id });
              }
            }}
          />
        ))}
      </ul>

      <AddStageForm
        onAdd={(name, stageType) =>
          addStage.mutateAsync({ pipelineId: pipeline.id, input: { name, stageType } })
        }
      />
    </div>
  );
}

/** Matches the CHECK constraint on fias_leads_pipelines.stale_after_days. */
const STALE_AFTER_DAYS_MIN = 1;
const STALE_AFTER_DAYS_MAX = 365;
/** Offered when enabling alerts on a pipeline that has never had a threshold. */
const STALE_AFTER_DAYS_DEFAULT = 14;

/**
 * Per-pipeline "alert me when a deal goes quiet" control.
 *
 * Off by default — the checkbox turning alerts on is what creates the
 * threshold, so no existing workspace starts emitting alerts because this
 * shipped. The day count commits on blur/Enter (matching the rename inputs
 * above) rather than on every keystroke, so typing "30" doesn't briefly
 * persist a threshold of 3.
 */
function StaleAlertSetting({
  pipeline,
  onChange,
}: {
  pipeline: FiasLeadsPipelineDto;
  onChange: (staleAfterDays: number | null) => Promise<unknown>;
}) {
  const enabled = pipeline.stale_after_days !== null;
  const [draft, setDraft] = useState(String(pipeline.stale_after_days ?? STALE_AFTER_DAYS_DEFAULT));

  // Re-seed when the server value changes (another tab, or a failed commit
  // that rolled back) so the field never disagrees with what's stored.
  useEffect(() => {
    setDraft(String(pipeline.stale_after_days ?? STALE_AFTER_DAYS_DEFAULT));
  }, [pipeline.stale_after_days]);

  const commitDays = async () => {
    const parsed = Number(draft);
    if (
      !Number.isInteger(parsed) ||
      parsed < STALE_AFTER_DAYS_MIN ||
      parsed > STALE_AFTER_DAYS_MAX
    ) {
      setDraft(String(pipeline.stale_after_days ?? STALE_AFTER_DAYS_DEFAULT));
      return;
    }
    if (parsed === pipeline.stale_after_days) return;
    try {
      await onChange(parsed);
    } catch (err) {
      setDraft(String(pipeline.stale_after_days ?? STALE_AFTER_DAYS_DEFAULT));
      alert(`Could not save the alert threshold: ${(err as Error).message}`);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded bg-muted/30 px-3 py-2 text-sm">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) =>
            onChange(e.target.checked ? Number(draft) || STALE_AFTER_DAYS_DEFAULT : null)
          }
          className="h-4 w-4"
        />
        <span>Alert me when a deal has no activity for</span>
      </label>
      <input
        type="number"
        value={draft}
        disabled={!enabled}
        min={STALE_AFTER_DAYS_MIN}
        max={STALE_AFTER_DAYS_MAX}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitDays}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        aria-label={`Days without activity before alerting on ${pipeline.name} deals`}
        className="w-20 rounded border border-border bg-background px-2 py-1 disabled:opacity-50"
      />
      <span className={enabled ? '' : 'text-muted-foreground'}>days</span>
      {!enabled && (
        <span className="text-xs text-muted-foreground">
          — no stale-deal alerts on this pipeline
        </span>
      )}
    </div>
  );
}

function PipelineNameEditor({
  pipeline,
  onRename,
}: {
  pipeline: FiasLeadsPipelineDto;
  onRename: (name: string) => Promise<unknown>;
}) {
  const [value, setValue] = useState(pipeline.name);

  // Re-seed when the server value changes (e.g. concurrent edit / refetch).
  useEffect(() => setValue(pipeline.name), [pipeline.name]);

  const commit = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === pipeline.name) {
      setValue(pipeline.name);
      return;
    }
    try {
      await onRename(trimmed);
    } catch (err) {
      setValue(pipeline.name);
      alert(`Could not rename pipeline: ${(err as Error).message}`);
    }
  };

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          setValue(pipeline.name);
          e.currentTarget.blur();
        }
      }}
      aria-label="Pipeline name"
      className="flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-lg font-medium hover:border-border focus:border-border focus:bg-background focus:outline-none"
    />
  );
}

function StageRow({
  stage,
  isFirst,
  isLast,
  reordering,
  onMoveUp,
  onMoveDown,
  onRename,
  onChangeType,
  onDelete,
}: {
  stage: FiasLeadsPipelineStageDto;
  isFirst: boolean;
  isLast: boolean;
  reordering: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRename: (name: string) => Promise<unknown>;
  onChangeType: (stageType: FiasLeadsPipelineStageType) => Promise<unknown>;
  onDelete: () => void;
}) {
  const [name, setName] = useState(stage.name);

  useEffect(() => setName(stage.name), [stage.name]);

  const commitName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === stage.name) {
      setName(stage.name);
      return;
    }
    try {
      await onRename(trimmed);
    } catch (err) {
      setName(stage.name);
      alert(`Could not rename stage: ${(err as Error).message}`);
    }
  };

  return (
    <li className="flex items-center gap-2 text-sm">
      <div className="flex flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst || reordering}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
          title="Move up"
          aria-label={`Move ${stage.name} up`}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast || reordering}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
          title="Move down"
          aria-label={`Move ${stage.name} down`}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
      <span className={`rounded px-2 py-0.5 text-xs ${STAGE_TYPE_COLORS[stage.stage_type]}`}>
        {stage.stage_type}
      </span>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setName(stage.name);
            e.currentTarget.blur();
          }
        }}
        aria-label="Stage name"
        className="flex-1 rounded border border-border bg-background px-2 py-1"
      />
      <select
        value={stage.stage_type}
        onChange={(e) => onChangeType(e.target.value as FiasLeadsPipelineStageType)}
        className="rounded border border-border bg-background px-2 py-1 text-xs"
        aria-label="Stage type"
      >
        <option value="open">open</option>
        <option value="won">won</option>
        <option value="lost">lost</option>
      </select>
      <button
        onClick={onDelete}
        className="text-muted-foreground hover:text-destructive"
        title="Delete stage"
        aria-label={`Delete ${stage.name}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  );
}

function AddStageForm({
  onAdd,
}: {
  onAdd: (name: string, stageType: FiasLeadsPipelineStageType) => Promise<unknown>;
}) {
  const [name, setName] = useState('');
  const [stageType, setStageType] = useState<FiasLeadsPipelineStageType>('open');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onAdd(name.trim(), stageType);
    setName('');
    setStageType('open');
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2 mt-1">
      <Plus className="h-4 w-4 text-muted-foreground" />
      <input
        type="text"
        placeholder="New stage name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm"
      />
      <select
        value={stageType}
        onChange={(e) => setStageType(e.target.value as FiasLeadsPipelineStageType)}
        className="rounded border border-border bg-background px-2 py-1 text-xs"
      >
        <option value="open">open</option>
        <option value="won">won</option>
        <option value="lost">lost</option>
      </select>
      <button
        type="submit"
        disabled={!name.trim()}
        className="rounded bg-primary px-3 py-1 text-primary-foreground text-xs disabled:opacity-50"
      >
        Add
      </button>
    </form>
  );
}
